'use client';
import { useSearchParams } from 'next/navigation';

import { useEffect } from 'react';

import { Box, Flex, Grid, useMediaQuery, useToken, VStack } from '@chakra-ui/react';

import { ListingAvailableOptions } from '@nocowanie/noc-api';

import { SEARCH_ENGINE } from '@nocowanie/core/consts';
import { DataLayerPageTypeEnum } from '@nocowanie/core/models';
import { mapToaster, Toaster } from '@nocowanie/common-ui/components/molecules/toaster';

import { Map } from '@app/components/templates/map-root';
import { MAP_DEFAULTS } from '@app/consts';
import { useMapSearch } from '@app/hooks/useMapSearch';
import { useMapToasts } from '@app/hooks/useMapToast';
import { usePageDataLayerEvents } from '@app/hooks/usePageDataLayerEvents';
import { usePagePath } from '@app/hooks/usePagePath';
import { useSearchInitializer } from '@app/hooks/useSearchInitializer';

export const MapContainer = ({
    availableOptions,
}: {
    availableOptions: ListingAvailableOptions | null;
}) => {
    const [mapSidebarWidth] = useToken('sizes', ['mapSidebarWidth']);
    const searchParams = useSearchParams();
    const pagePath = usePagePath();
    const initializeMapSearch = useSearchInitializer({
        autoOpenFiltersOnDesktop: true,
    });
    const {
        searchCriteria,
        filters,
        hasGeoChanged,
        isFiltersContentVisible,
        isMobileRes,
        setMapLocationDetails,
        setMapPageTitle,
        fetchAccommodations,
        mapAccommodations,
        isLoadingAccommodations,
        isMapLoaded,
        accommodationsError,
    } = useMapSearch();

    useMapToasts({
        accommodations: mapAccommodations,
        isLoadingAccommodations,
        accommodationsError,
        isMobileRes,
        isFiltersContentVisible,
    });

    usePageDataLayerEvents({
        pageType: DataLayerPageTypeEnum.Map,
        pagePath,
    });

    useEffect(() => {
        const params = new URLSearchParams(searchParams?.toString());
        if (!params.get('location')) {
            params.set('location', MAP_DEFAULTS.LOCATION);
        }
        if (!params.get('type')) {
            params.set('type', MAP_DEFAULTS.LOCATION_TYPE);
        }
        if (!params.get('geoId')) {
            params.set('geoId', MAP_DEFAULTS.GEO_ID);
        }
        initializeMapSearch(params, undefined, isMobileRes);
    }, [isMobileRes]);

    useEffect(() => {
        if (!searchCriteria.geoId || !searchCriteria.locationType) return;
        setMapLocationDetails(searchCriteria.geoId, searchCriteria.locationType);
    }, [searchCriteria.geoId, searchCriteria.locationType, setMapLocationDetails]);

    useEffect(() => {
        fetchAccommodations();
    }, [
        filters,
        searchCriteria.checkInDate,
        searchCriteria.checkOutDate,
        searchCriteria.guests.adultsCount,
        searchCriteria.guests.childrenCount,
        searchCriteria.flexDates,
    ]);

    const [isMediumBreakpoint] = useMediaQuery(['(max-width: 1450px)']);
    useEffect(() => {
        if (hasGeoChanged) {
            setMapPageTitle(
                searchCriteria.geoId ?? undefined,
                searchCriteria.locationType ?? undefined,
            );
        }
    }, [hasGeoChanged, searchCriteria.geoId, searchCriteria.locationType, setMapPageTitle]);

    return (
        <Map.Root>
            <Flex direction="column" h={'100dvh'}>
                {isMobileRes ? (
                    <>
                        <Box h={'85px'}>
                            <Map.CloseButton
                                hidden={!isMapLoaded}
                                height={'2.5rem'}
                                my={1}
                                ml={1}
                            />
                        </Box>
                        <VStack position={'relative'} flex={1} alignItems={'flex-start'}>
                            <Map.SearchBar
                                zIndex="overMap"
                                px={4}
                                mt={-10}
                                minW={'100%'}
                                isMapLoaded={isMapLoaded}
                            />
                            <Map.ActiveFilters zIndex="overMap" isMapLoaded={isMapLoaded} />
                            <Map.MapWrapper />
                            <Map.Filters availableOptions={availableOptions} />
                        </VStack>
                    </>
                ) : (
                    <Flex flex={1}>
                        <Map.CloseButton
                            zIndex="overMap"
                            position={'absolute'}
                            top={'2rem'}
                            right={3}
                            isMapLoaded={isMapLoaded}
                        />
                        <Flex position="relative" zIndex="overMap">
                            <Map.Filters
                                isMapLoaded={isMapLoaded}
                                availableOptions={availableOptions}
                            />
                        </Flex>
                        <VStack flex={1} position={'relative'}>
                            <Grid templateColumns="1fr auto 1fr" mt={4}>
                                <Map.SearchBar
                                    isMapLoaded={isMapLoaded}
                                    gridColumn="2"
                                    w={isMediumBreakpoint ? '60dvw' : SEARCH_ENGINE.MAX_WIDTH}
                                    zIndex="docked"
                                />
                                <Map.ActiveFilters
                                    isMapLoaded={isMapLoaded}
                                    gridColumn="2 / 4"
                                    zIndex="overMap"
                                />
                            </Grid>
                            <Map.MapWrapper />
                        </VStack>
                    </Flex>
                )}
            </Flex>
            <Toaster
                toasterInstance={mapToaster}
                variant="dark"
                transform={
                    isMobileRes ? undefined : `translateX(${parseFloat(mapSidebarWidth) / 2}px)`
                }
            />
        </Map.Root>
    );
};
